import React, { useState, useEffect } from 'react';
import { useApp } from '../../context/AppContext';
import apiService from '../../services/apiService';
import { Package } from 'lucide-react';
import './PreparacionPage.css';

const PreparacionPage = () => {
  const { loading, setLoading, showToast, user, activeWarehouse } = useApp();

  const [activeTab, setActiveTab] = useState('preparacion'); // 'preparacion' o 'porPedido'
  const [codigoBarras, setCodigoBarras] = useState('');
  const [productosPicklist, setProductosPicklist] = useState([]);
  const [productosPreparados, setProductosPreparados] = useState([]);

  useEffect(() => {
    loadData();
  }, [activeWarehouse]); // Recargar cuando cambie el almacén activo

  const loadData = async () => {
    try {
      setLoading(true);

      // Verificar que haya un almacén activo seleccionado
      if (!activeWarehouse || !activeWarehouse.id) {
        showToast('Selecciona un almacén para ver los productos', 'warning');
        setProductosPicklist([]);
        setProductosPreparados([]);
        setLoading(false);
        return;
      }

      const almacenActivo = activeWarehouse.id;

      // Obtener ventas en estado "Picking"
      const result = await apiService.getVentas();

      if (result.success) {
        const ventasPicking = result.data.filter(venta => venta.estado === 'Picking');

        // Obtener productos de todas las ventas en picking
        const productosPromises = ventasPicking.map(async (venta) => {
          const detalleResult = await apiService.getVenta(venta.id);
          if (detalleResult.success && detalleResult.data.detalles) {
            return detalleResult.data.detalles.map(producto => ({
              ...producto,
              venta_id: venta.id,
              venta_folio: venta.folio,
              cliente_nombre: venta.cliente_nombre,
              tipo_entrega: venta.tipo_entrega
            }));
          }
          return [];
        });

        const productosArrays = await Promise.all(productosPromises);
        const todosProductos = productosArrays.flat();

        // Filtrar por almacén activo y separar por cantidad escaneada
        const productosFiltrados = todosProductos.filter(p => {
          // Mostrar solo productos del almacén activo
          // Si no tiene almacen_id, no incluirlo (seguridad)
          return p.almacen_id && p.almacen_id === almacenActivo;
        });

        // Izquierda: productos que NO están completamente escaneados (cantidad_escaneada < cantidad)
        const pendientes = productosFiltrados.filter(p => {
          const escaneado = p.cantidad_escaneada || 0;
          return escaneado < p.cantidad;
        });

        // Derecha: productos con al menos 1 escaneo (cantidad_escaneada > 0)
        const preparados = productosFiltrados.filter(p => (p.cantidad_escaneada || 0) > 0);

        // Ordenar lista derecha: incompletos primero, completos al final
        preparados.sort((a, b) => {
          const aCompleto = (a.cantidad_escaneada || 0) >= a.cantidad;
          const bCompleto = (b.cantidad_escaneada || 0) >= b.cantidad;

          // Si uno está completo y el otro no, el incompleto va primero
          if (aCompleto && !bCompleto) return 1;
          if (!aCompleto && bCompleto) return -1;

          // Si ambos tienen el mismo estado, ordenar por venta_id (más antiguo primero)
          return a.venta_id - b.venta_id;
        });

        setProductosPicklist(pendientes);
        setProductosPreparados(preparados);
      } else {
        showToast(result.message || 'Error al cargar productos', 'error');
      }
    } catch (error) {
      console.error('Error al cargar datos:', error);
      showToast('Error al cargar datos', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleEscanearCodigo = async (e) => {
    if (e.key === 'Enter' && codigoBarras.trim()) {
      try {
        // Buscar producto por código de barras
        const result = await apiService.buscarProductoPorCodigo(codigoBarras.trim());

        if (result.success && result.data) {
          const producto_id = result.data.id;

          // Buscar TODOS los productos con este producto_id en la lista de picklist
          const productosDelMismoTipo = productosPicklist.filter(p => p.producto_id === producto_id);

          if (productosDelMismoTipo.length > 0) {
            // Ordenar del pedido más antiguo al más nuevo (por venta_id)
            productosDelMismoTipo.sort((a, b) => a.venta_id - b.venta_id);

            // Encontrar el primer producto que no esté completamente escaneado
            const productoParaEscanear = productosDelMismoTipo.find(p =>
              (p.cantidad_escaneada || 0) < p.cantidad
            );

            if (productoParaEscanear) {
              // Incrementar cantidad escaneada
              await handleIncrementarEscaneo(productoParaEscanear);
              setCodigoBarras(''); // Limpiar input
            } else {
              showToast('Todos los productos de este tipo ya están completos', 'info');
              setCodigoBarras('');
            }
          } else {
            showToast('Producto no encontrado en la lista de picking', 'warning');
            setCodigoBarras('');
          }
        } else {
          showToast('Código de barras no encontrado', 'warning');
          setCodigoBarras('');
        }
      } catch (error) {
        console.error('Error al buscar código:', error);
        showToast('Error al buscar producto', 'error');
        setCodigoBarras('');
      }
    }
  };

  const verificarYActualizarPedidoCompleto = async (venta_id) => {
    try {
      // Obtener detalles completos del pedido
      const ventaResult = await apiService.getVenta(venta_id);

      if (ventaResult.success && ventaResult.data.detalles) {
        const detalles = ventaResult.data.detalles;

        // Verificar si todos los productos del pedido están completamente escaneados
        const todosCompletos = detalles.every(detalle =>
          (detalle.cantidad_escaneada || 0) >= detalle.cantidad
        );

        if (todosCompletos) {
          // Actualizar estado de la venta a "Preparado"
          const updateResult = await apiService.actualizarEstadoVenta(venta_id, 'Preparado');

          if (updateResult.success) {
            showToast(`¡Pedido #${ventaResult.data.folio} completado y marcado como Preparado!`, 'success');
          }
        }
      }
    } catch (error) {
      console.error('Error al verificar pedido completo:', error);
    }
  };

  const handleIncrementarEscaneo = async (producto) => {
    try {
      setLoading(true);

      const venta_id = producto.venta_id;

      // Llamar al API para incrementar cantidad escaneada
      const result = await apiService.actualizarEstatusPreparacion(producto.id, 'pendiente');

      if (result.success) {
        const { cantidad_escaneada, cantidad_total, completado } = result.data;

        // Mostrar mensaje apropiado
        if (completado) {
          showToast(`Producto completado (${cantidad_escaneada}/${cantidad_total})`, 'success');
        } else {
          showToast(`Escaneado: ${cantidad_escaneada}/${cantidad_total}`, 'info');
        }

        // Verificar si el pedido completo está listo y actualizar su estado
        await verificarYActualizarPedidoCompleto(venta_id);

        // Recargar datos completos desde el servidor para refrescar la lista
        await loadData();
      } else {
        showToast(result.message || 'Error al actualizar cantidad escaneada', 'error');
        setLoading(false);
      }
    } catch (error) {
      console.error('Error al incrementar escaneo:', error);
      showToast('Error al incrementar escaneo', 'error');
      setLoading(false);
    }
  };

  const handleMoverAPreparados = async (producto) => {
    try {
      setLoading(true);

      const venta_id = producto.venta_id;

      // Marcar como preparado completando la cantidad total (cantidad_escaneada = cantidad)
      const result = await apiService.actualizarEstatusPreparacion(producto.id, 'preparado', false, true);

      if (result.success) {
        showToast('Producto marcado como preparado', 'success');

        // Verificar si el pedido completo está listo y actualizar su estado
        await verificarYActualizarPedidoCompleto(venta_id);

        // Recargar datos completos desde el servidor para refrescar la lista
        await loadData();
      } else {
        showToast(result.message || 'Error al actualizar estatus', 'error');
        setLoading(false);
      }
    } catch (error) {
      console.error('Error al actualizar estatus:', error);
      showToast('Error al actualizar estatus', 'error');
      setLoading(false);
    }
  };

  const handleVolverAPicklist = async (producto) => {
    try {
      setLoading(true);

      // Reiniciar cantidad escaneada a 0
      const result = await apiService.reiniciarEscaneoProducto(producto.id);

      if (result.success) {
        showToast('Escaneo reiniciado', 'info');

        // Recargar datos completos desde el servidor para refrescar la lista
        await loadData();
      } else {
        showToast(result.message || 'Error al reiniciar escaneo', 'error');
        setLoading(false);
      }
    } catch (error) {
      console.error('Error al reiniciar escaneo:', error);
      showToast('Error al reiniciar escaneo', 'error');
      setLoading(false);
    }
  };

  const handleMarcarTodosComoPreparados = async () => {
    try {
      setLoading(true);

      // Obtener todos los productos de la lista de preparados
      const productosParaCompletar = productosPreparados.filter(p => {
        const escaneado = p.cantidad_escaneada || 0;
        return escaneado < p.cantidad; // Solo los que NO están completos
      });

      if (productosParaCompletar.length === 0) {
        showToast('Todos los productos ya están completos', 'info');
        setLoading(false);
        return;
      }

      let exitosos = 0;
      let fallidos = 0;
      const ventasAfectadas = new Set();

      // Marcar cada producto como preparado completando la cantidad total
      for (const producto of productosParaCompletar) {
        try {
          const result = await apiService.actualizarEstatusPreparacion(producto.id, 'preparado', false, true);
          if (result.success) {
            exitosos++;
            ventasAfectadas.add(producto.venta_id);
          } else {
            fallidos++;
          }
        } catch (error) {
          console.error(`Error al marcar producto ${producto.id}:`, error);
          fallidos++;
        }
      }

      // Verificar pedidos completos y actualizar sus estados
      for (const venta_id of ventasAfectadas) {
        await verificarYActualizarPedidoCompleto(venta_id);
      }

      // Mostrar resultado
      if (exitosos > 0 && fallidos === 0) {
        showToast(`${exitosos} producto(s) marcado(s) como preparado`, 'success');
      } else if (exitosos > 0 && fallidos > 0) {
        showToast(`${exitosos} marcado(s), ${fallidos} fallido(s)`, 'warning');
      } else {
        showToast('No se pudo marcar ningún producto', 'error');
      }

      // Recargar datos
      await loadData();
    } catch (error) {
      console.error('Error al marcar productos:', error);
      showToast('Error al marcar productos', 'error');
      setLoading(false);
    }
  };

  return (
    <div className="preparacion-page">
      {/* Header */}
      <div className="preparacion-header">
        <div>
          <h1>
            Preparación de Pedidos para el almacén "{activeWarehouse?.nombre || 'Sin seleccionar'}"
          </h1>
        </div>
      </div>

      {/* Tabs */}
      <div className="preparacion-tabs">
        <button
          className={`tab-btn ${activeTab === 'preparacion' ? 'active' : ''}`}
          onClick={() => setActiveTab('preparacion')}
        >
          <Package size={18} />
          <span>Preparación</span>
        </button>
        <button
          className={`tab-btn ${activeTab === 'porPedido' ? 'active' : ''}`}
          onClick={() => setActiveTab('porPedido')}
        >
          <Package size={18} />
          <span>Por Pedido</span>
        </button>
      </div>

      {/* Contenido según tab activo */}
      {activeTab === 'preparacion' ? (
        <div className="preparacion-content">
        {/* Columna Izquierda - Picklist (productos de pedidos en Picking) */}
        <div className="preparacion-column">
          <div className="column-header">
            <Package size={18} />
            <h2>Picklist - Pedidos en Picking</h2>
            <span className="badge">{productosPicklist.length}</span>
          </div>

          {/* Input para escanear código de barras */}
          <div className="barcode-scanner">
            <input
              type="text"
              placeholder="Escanear código de barras..."
              value={codigoBarras}
              onChange={(e) => setCodigoBarras(e.target.value)}
              onKeyDown={handleEscanearCodigo}
              className="barcode-input"
              autoFocus
            />
          </div>

          <div className="productos-list">
            {productosPicklist.length === 0 ? (
              <div className="empty-state">
                <Package size={48} />
                <p>No hay productos en picking</p>
                <small>Los productos de pedidos en estado "Picking" aparecerán aquí</small>
              </div>
            ) : (
              productosPicklist.map((producto, index) => (
                <div key={`pick-${producto.venta_id}-${producto.producto_id}-${index}`} className="producto-card-simple">
                  <div className="producto-info-simple">
                    <span className="producto-nombre-simple">{producto.producto_nombre}</span>
                    {producto.codigo_corto && (
                      <span className="codigo-item">C: {producto.codigo_corto}</span>
                    )}
                    {producto.codigo_barras && (
                      <span className="codigo-item">B: {producto.codigo_barras}</span>
                    )}
                    <span className="producto-cantidad-simple">
                      {producto.cantidad_escaneada || 0}/{producto.cantidad}
                    </span>
                  </div>
                  <button
                    className="producto-action-btn prepare-btn"
                    onClick={() => handleMoverAPreparados(producto)}
                    title="Marcar como preparado"
                  >
                    ✓
                  </button>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Columna Derecha - Productos Escaneados */}
        <div className="preparacion-column">
          <div className="column-header">
            <Package size={18} />
            <h2>Productos Escaneados</h2>
            <span className="badge">{productosPreparados.length}</span>
            {productosPreparados.length > 0 && (
              <button
                className="producto-action-btn prepare-btn"
                onClick={handleMarcarTodosComoPreparados}
                title="Marcar todos como preparados"
                style={{ marginLeft: 'auto', padding: '6px 12px', fontSize: '13px' }}
                disabled={loading}
              >
                ✓ Marcar Todos
              </button>
            )}
          </div>

          <div className="productos-list">
            {productosPreparados.length === 0 ? (
              <div className="empty-state">
                <Package size={48} />
                <p>No hay productos preparados</p>
              </div>
            ) : (
              productosPreparados.map((producto, index) => {
                const cantidadEscaneada = producto.cantidad_escaneada || 0;
                const cantidadTotal = producto.cantidad;
                const completado = cantidadEscaneada >= cantidadTotal;

                return (
                  <div
                    key={`prep-${producto.venta_id}-${producto.producto_id}-${index}`}
                    className={`producto-card-simple ${completado ? 'completado' : 'en-proceso'}`}
                  >
                    <div className="producto-info-simple">
                      <span className="producto-nombre-simple">{producto.producto_nombre}</span>
                      {producto.codigo_corto && (
                        <span className="codigo-item">C: {producto.codigo_corto}</span>
                      )}
                      {producto.codigo_barras && (
                        <span className="codigo-item">B: {producto.codigo_barras}</span>
                      )}
                      <span className={`producto-cantidad-simple ${completado ? 'completo' : 'parcial'}`}>
                        {cantidadEscaneada}/{cantidadTotal}
                      </span>
                    </div>
                    <button
                      className="producto-action-btn reiniciar-btn"
                      onClick={() => handleVolverAPicklist(producto)}
                      title="Reiniciar escaneo"
                    >
                      ↺
                    </button>
                  </div>
                );
              })
            )}
          </div>
        </div>
      </div>
      ) : (
        /* Segunda Tab - Vista por Pedido */
        <div className="preparacion-content" style={{ gridTemplateColumns: '1fr' }}>
          <div className="preparacion-column" style={{ gridColumn: '1 / -1' }}>
            <div className="column-header">
              <Package size={18} />
              <h2>Vista por Pedido</h2>
            </div>

            <div className="pedidos-list">
              {(() => {
                // Agrupar productos por venta_id
                const productosAgrupados = [...productosPicklist, ...productosPreparados].reduce((acc, producto) => {
                  const ventaId = producto.venta_id;
                  if (!acc[ventaId]) {
                    acc[ventaId] = {
                      venta_id: ventaId,
                      venta_folio: producto.venta_folio,
                      cliente_nombre: producto.cliente_nombre,
                      tipo_entrega: producto.tipo_entrega,
                      productos: []
                    };
                  }
                  acc[ventaId].productos.push(producto);
                  return acc;
                }, {});

                const pedidos = Object.values(productosAgrupados);

                if (pedidos.length === 0) {
                  return (
                    <div className="empty-state">
                      <Package size={48} />
                      <p>No hay pedidos en picking</p>
                    </div>
                  );
                }

                return pedidos.map((pedido) => {
                  // Calcular progreso del pedido
                  const totalProductos = pedido.productos.length;
                  const productosCompletados = pedido.productos.filter(p =>
                    (p.cantidad_escaneada || 0) >= p.cantidad
                  ).length;
                  const pedidoCompleto = productosCompletados === totalProductos;

                  return (
                    <div key={`pedido-${pedido.venta_id}`} className={`pedido-card ${pedidoCompleto ? 'completado' : 'en-proceso'}`}>
                      {/* Header del pedido */}
                      <div className="pedido-header">
                        <div className="pedido-info">
                          <span className="pedido-folio">#{pedido.venta_folio}</span>
                          <span className="pedido-cliente">{pedido.cliente_nombre}</span>
                          {pedido.tipo_entrega && (
                            <span className={`pedido-entrega ${pedido.tipo_entrega.toLowerCase()}`}>
                              {pedido.tipo_entrega}
                            </span>
                          )}
                        </div>
                        <div className="pedido-progreso">
                          <span className={`progreso-badge ${pedidoCompleto ? 'completo' : 'parcial'}`}>
                            {productosCompletados}/{totalProductos}
                          </span>
                        </div>
                      </div>

                      {/* Lista de productos del pedido */}
                      <div className="pedido-productos">
                        {pedido.productos.map((producto, index) => {
                          const cantidadEscaneada = producto.cantidad_escaneada || 0;
                          const cantidadTotal = producto.cantidad;
                          const completado = cantidadEscaneada >= cantidadTotal;

                          return (
                            <div
                              key={`pedido-prod-${producto.id}-${index}`}
                              className={`pedido-producto-item ${completado ? 'completado' : cantidadEscaneada > 0 ? 'en-proceso' : 'pendiente'}`}
                            >
                              <div className="pedido-producto-info">
                                <span className="pedido-producto-nombre">{producto.producto_nombre}</span>
                                {producto.codigo_corto && (
                                  <span className="codigo-item-small">C: {producto.codigo_corto}</span>
                                )}
                                {producto.codigo_barras && (
                                  <span className="codigo-item-small">B: {producto.codigo_barras}</span>
                                )}
                              </div>
                              <div className="pedido-producto-progreso">
                                <span className={`progreso-text ${completado ? 'completo' : cantidadEscaneada > 0 ? 'parcial' : 'pendiente'}`}>
                                  {cantidadEscaneada}/{cantidadTotal}
                                </span>
                                {completado && <span className="check-icon">✓</span>}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                });
              })()}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PreparacionPage;
